import { z } from 'zod';

import { SchemaValidationFailedError } from '@kilat/shared';
import { callFunction } from './_lazy.js';

/**
 * Typed wrapper around the `submitInquiry` callable (public `/contact` form).
 * Mirrors the server schema client-side so the visitor gets immediate feedback
 * before a round-trip.
 *
 * The SDK loads at call time via `callFunction` — see `_lazy.ts` for why.
 */

export const SubmitInquiryInputSchema = z.object({
  name: z.string().min(1).max(100),
  email: z.string().email(),
  message: z.string().min(1).max(2000),
  locale: z.enum(['en', 'ms', 'zh', 'ta']).default('en'),
  platformBrandId: z.string().min(1).max(64),
  /**
   * Consent for the agent-lead path (G25). Defaults false so an omitted field
   * can never read as agreement to be contacted by a commissioned salesperson;
   * the server schema defaults the same way rather than trusting this one.
   */
  wantsRepresentativeContact: z.boolean().default(false),
});
export type SubmitInquiryInput = z.infer<typeof SubmitInquiryInputSchema>;

export const SubmitInquiryOutputSchema = z.object({ ok: z.boolean(), id: z.string() });
export type SubmitInquiryOutput = z.infer<typeof SubmitInquiryOutputSchema>;

export async function callSubmitInquiry(input: SubmitInquiryInput): Promise<SubmitInquiryOutput> {
  const parsed = SubmitInquiryInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('submitInquiry.input', parsed.error.flatten());
  }
  const result = await callFunction<SubmitInquiryInput, SubmitInquiryOutput>(
    'submitInquiry',
    parsed.data,
  );
  const out = SubmitInquiryOutputSchema.safeParse(result);
  if (!out.success) {
    throw new SchemaValidationFailedError('submitInquiry.output', out.error.flatten());
  }
  return out.data;
}
